import { Box, Button, Dialog, DialogContent, DialogTitle, FormControl, Grid, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material';
import { DataGrid, gridClasses, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import GroupIcon from '@mui/icons-material/Group';
import CloseIcon from '@mui/icons-material/Close';
import Loading from '../../Shared/Loading';
import { localDateFormat } from '../../../utils/timeFormat';
import { isDefaultDate, generateRandomId } from '../../../utils/common';
import { fetchPopulationList } from '../../../store/adminMembers.slice';
import { fetchHealthPlans } from '../../../store/memberDetail.slice';
import {
    fetchPopulationUploadHistoryReport,
    exportPopulationUploadHistoryReport,
    fetchMembersByPopulation,
    exportUploadedMembersByPopulation,
    setModalVisibility,
    resetData,
} from '../../../store/populationUploadReport.slice';

const PopulationUploadHistoryReport = () => {
    const { rowData, memberData, modalVisibility, isExporting } = useSelector((state: RootState) => state.populationUploadReport);
    const { downLoading } = useSelector((state: RootState) => state.shared);
    const { population } = useSelector((state: RootState) => state.adminMembers);
    const { healthPlans } = useSelector((state: RootState) => state.memberDetail);
    const dispatch = useAppDispatch();
    const [pageSize, setPageSize] = useState(15);
    const [dialogPageSize, setDialogPageSize] = useState(15);
    const [page, setPage] = useState(0);
    const [dialogPage, setDialogPage] = useState(0);
    const [healthPlan, setHealthPlan] = useState('All');
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [selectedIds, setSelectedIds] = useState([]);
    const [healthPlanName, setHealthPlanName] = useState('All');
    const [selectedPopulationId, setSelectedPopulationId] = useState('');

    const columns: GridColDef[] = [
        {
            field: 'Members',
            headerName: 'Members',
            flex: 1,
            minWidth: 80,
            renderCell: (params: GridRenderCellParams) => {
                return <GroupIcon color="primary" style={{ cursor: 'pointer', paddingLeft: '15px' }} onClick={(event) => openModal(event, params)} />;
            },
        },
        {
            field: 'HealthPlanId',
            headerName: 'Health Plan',
            minWidth: 200,
            flex: 1,
            valueGetter: (value, row) => `${healthPlans.find((p) => p.Id === row.HealthPlanId)?.Name}`,
        },
        {
            field: 'PopulationId',
            headerName: 'Population',
            minWidth: 200,
            flex: 1,
            valueGetter: (value, row) => `${population.find((p) => p.Id === row.PopulationId)?.PopulationName}`,
        },
        {
            field: 'UploadedOn',
            headerName: 'Uploaded On',
            minWidth: 130,
            flex: 1,
            valueGetter: (value, row) => `${isDefaultDate(row.UploadedOn) ? '-' : localDateFormat(new Date(row.UploadedOn))}`,
        },
        {
            field: 'TotalMembers',
            headerName: 'Total Members',
            minWidth: 130,
            flex: 1,
        },
        {
            field: 'TotalInserted',
            headerName: 'Totally Inserted',
            minWidth: 130,
            flex: 1,
            valueGetter: (value, row) => `${row.TotalInserted?.length}`,
        },
        {
            field: 'NewlyInserted',
            headerName: 'Newly Inserted',
            minWidth: 130,
            flex: 1,
            valueGetter: (value, row) => `${row.NewlyInserted?.length}`,
        },
        {
            field: 'Skipped',
            headerName: 'Skipped',
            minWidth: 110,
            flex: 1,
            valueGetter: (value, row) => `${row.Skipped?.length}`,
        },
        {
            field: 'UpdatedToUnreachableAndInserted',
            headerName: 'Updated To Unreachable And Inserted',
            minWidth: 260,
            flex: 1,
            valueGetter: (value, row) => `${row.UpdatedToUnreachableAndInserted?.length}`,
        },
        {
            field: 'HRAMemberWithFullProgramReferralTag',
            headerName: 'HRA Member With Full Program Referral Tag',
            minWidth: 310,
            flex: 1,
            valueGetter: (value, row) => `${row.HRAMemberWithFullProgramReferralTag?.length}`,
        },
        {
            field: 'FUHFUMMemberWithFullProgramReferralTag',
            headerName: 'FUHFUM Member With Full Program Referral Tag',
            minWidth: 340,
            flex: 1,
            valueGetter: (value, row) => `${row.FUHFUMMemberWithFullProgramReferralTag?.length}`,
        },
        {
            field: 'FullProgramMemberWithHRAReferralTag',
            headerName: 'Full Program Member With HRA Referral Tag',
            minWidth: 320,
            flex: 1,
            valueGetter: (value, row) => `${row.FullProgramMemberWithHRAReferralTag?.length}`,
        },
        {
            field: 'FUHFUMMemberWithHRAReferralTag',
            headerName: 'FUHFUM Member With HRA Referral Tag',
            minWidth: 300,
            flex: 1,
            valueGetter: (value, row) => `${row.FUHFUMMemberWithHRAReferralTag?.length}`,
        },
        {
            field: 'FullProgramMemberWithFUHFUMReferralTag',
            headerName: 'Full Program Member With FUHFUM Referral Tag',
            minWidth: 350,
            flex: 1,
            valueGetter: (value, row) => `${row.FullProgramMemberWithFUHFUMReferralTag?.length}`,
        },
        {
            field: 'HRAMemberWithFUHFUMReferralTag',
            headerName: 'HRA Member With FUHFUM Referral Tag',
            minWidth: 300,
            flex: 1,
            valueGetter: (value, row) => `${row.HRAMemberWithFUHFUMReferralTag?.length}`,
        },
    ];

    const openModal = (event, params) => {
        event.stopPropagation();
        dispatch(fetchMembersByPopulation(params.row.Id));
        setSelectedPopulationId(params.row.Id);
        setHealthPlanName(
            `${healthPlans.find((p) => p.Id === params.row.HealthPlanId)?.Name} - ${population.find((p) => p.Id === params.row.PopulationId)?.PopulationName}`
        );
    };
    const closeModal = () => dispatch(setModalVisibility(false));

    // Modal Columns
    const modalColumns: GridColDef[] = [
        {
            field: 'AhcccsId',
            headerName: 'AHCCCS Id',
            minWidth: 110,
            flex: 1,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 180,
            flex: 1.4,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 160,
            flex: 1.4,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 120,
            flex: 0.8,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : localDateFormat(new Date(row.DOB))}`,
        },
        {
            field: 'HealthPlan',
            headerName: 'Health Plan',
            minWidth: 160,
            flex: 2,
        },
        {
            field: 'Population',
            headerName: 'Population',
            minWidth: 160,
            flex: 2,
        },
        {
            field: 'Status',
            headerName: 'Status',
            minWidth: 180,
            flex: 2,
        },
        {
            field: 'IsInHra',
            headerName: 'IsInHra',
            minWidth: 100,
            flex: 0.5,
            valueGetter: (value, row) => `${row.IsInHra === true ? 'Yes' : 'No'}`,
        },
        {
            field: 'IsInFuhFum',
            headerName: 'IsInFuhFum',
            minWidth: 100,
            flex: 0.5,
            valueGetter: (value, row) => `${row.IsInFuhFum === true ? 'Yes' : 'No'}`,
        },
    ];

    const handleWindowResize = () => {
        setPageSize(parseInt(window.innerHeight / (window.innerHeight > 1000 ? 72 : 74) + ''));
    };

    useEffect(() => {
        dispatch(fetchPopulationList());
        dispatch(fetchHealthPlans());
        handleWindowResize();
        window.addEventListener('resize', handleWindowResize, false);
        return () => {
            dispatch(resetData());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRowSelection = (selectionModel) => {
        setSelectedIds(selectionModel);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog open={modalVisibility} onClose={closeModal} fullWidth maxWidth="xl">
                <DialogTitle>
                    <Typography fontWeight="fontWeightMedium" variant="h5" textAlign={'center'}>
                        {healthPlanName}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={closeModal}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Button
                        style={{ minWidth: '120px', float: 'right' }}
                        onClick={() => dispatch(exportUploadedMembersByPopulation(selectedPopulationId))}
                        variant="contained"
                        color="success"
                    >
                        {isExporting ? 'Downloading..' : 'Export'}
                    </Button>
                    {memberData.map((data) => (
                        <Box sx={{ width: '100%', minHeight: '320px' }}>
                            <Typography fontWeight="fontWeightMedium" component="h6">
                                {data.Label}
                            </Typography>
                            <DataGrid
                                rows={data.Members}
                                columns={modalColumns}
                                paginationModel={{ pageSize: dialogPageSize, page: dialogPage }}
                                onPaginationModelChange={(model) => {
                                    setDialogPageSize(model.pageSize);
                                    setDialogPage(model.page);
                                }}
                                pageSizeOptions={[5]}
                                getRowId={(row) => `${row.Id}-${generateRandomId()}`}
                                getRowHeight={() => 'auto'}
                                sx={{
                                    marginY: '1rem',
                                    [`& .${gridClasses.cell}`]: {
                                        py: 2,
                                    },
                                    minHeight: '260px',
                                }}
                            />
                        </Box>
                    ))}
                </DialogContent>
            </Dialog>

            <Grid container>
                <Box width={'100%'} borderBottom={1} paddingBottom={1}>
                    <Typography fontWeight="fontWeightMedium" component="h3">
                        Population Upload Report
                    </Typography>
                </Box>
                {<Loading message={'Loading...'} />}
                <Grid size={12}>
                    <Stack width="100%" direction="row" alignItems="center" marginY={2} className="float-right" spacing={3}>
                        <FormControl className="form-control-200">
                            <Select value={healthPlan} onChange={(e) => setHealthPlan(e.target.value)}>
                                <MenuItem key="All" value="All">
                                    All
                                </MenuItem>
                                {healthPlans.map((plan) => (
                                    <MenuItem key={plan.Id} value={plan.Id}>
                                        {plan.Name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl className="form-control-200">
                            <DesktopDatePicker
                                label="Start Date"
                                value={fromDate}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => {
                                    setFromDate(newValue);
                                }}
                                slotProps={{ textField: {} }}
                            />
                        </FormControl>
                        <FormControl className="form-control-200">
                            <DesktopDatePicker
                                label="End Date"
                                value={toDate}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => {
                                    setToDate(newValue);
                                }}
                                slotProps={{ textField: {} }}
                            />
                        </FormControl>
                        <Button
                            onClick={() => dispatch(fetchPopulationUploadHistoryReport(healthPlan, fromDate, toDate))}
                            style={{ minWidth: '120px' }}
                            variant="contained"
                        >
                            Search
                        </Button>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => dispatch(exportPopulationUploadHistoryReport(healthPlan, fromDate, toDate, selectedIds))}
                            variant="contained"
                            color="success"
                        >
                            {downLoading ? 'Downloading..' : 'Export'}
                        </Button>
                    </Stack>
                </Grid>
                <Grid style={{ height: '84vh', width: '100%' }}>
                    <DataGrid
                        rows={rowData}
                        columns={columns}
                        paginationModel={{ pageSize: pageSize, page: page }}
                        pageSizeOptions={[5]}
                        checkboxSelection
                        onRowSelectionModelChange={handleRowSelection}
                        onPaginationModelChange={(model) => {
                            setPageSize(model.pageSize);
                            setPage(model.page);
                        }}
                        getRowId={(row) => `${row.Id}`}
                        getRowHeight={() => 'auto'}
                        sx={{
                            [`& .${gridClasses.cell}`]: {
                                py: 2,
                            },
                        }}
                    />
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
};

export default PopulationUploadHistoryReport;
